import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Container, Dropdown, DropdownButton, Form, InputGroup, Row, Tab, Tabs, Alert } from "react-bootstrap";
import { TIME_SCALE_MAP, getAbsoluteTimeFromRelativeTime, getDateStrFromDate, getLocalizedDate, getTimeStrFromDate } from '../../utils/time';
import { DateType } from './dateTypes';

interface CustomDateRangeSelectorProps {
    setShow: (show: boolean) => void;
    filterDateOptions: any;
    setFilterDateOptions: (options: any) => void;
    maxRangeInDays?: number; // Optional: maximum allowed range in days
}

/**
 * Dropdown that allows to select a single items. Select choices are applied immediately.
 */
export const CustomDateRangeSelector = ({ 
    setShow, 
    filterDateOptions, 
    setFilterDateOptions,
    maxRangeInDays 
}: CustomDateRangeSelectorProps) => {
    const wrapperRef: any = useRef(null);
    const [localType, setLocalType] = useState<string>(filterDateOptions["dateOptionType"]);
    const [localRelativeStart, setLocalRelativeStart] = useState<number | null>(filterDateOptions["relativeStart"]);
    const [localRelativeStartScale, setLocalRelativeStartScale] = useState<string>(filterDateOptions["relativeStartScale"]);
    const [localRelativeEnd, setLocalRelativeEnd] = useState<number>(filterDateOptions["relativeEnd"]);
    const [localRelativeEndScale, setLocalRelativeEndScale] = useState<string>(filterDateOptions["relativeEndScale"]);
    const [localAbsoluteStartDate, setLocalAbsoluteStartDate] = useState<string>(getDateStrFromDate(filterDateOptions["absoluteStart"]));
    const [localAbsoluteStartTime, setLocalAbsoluteStartTime] = useState<string>(getTimeStrFromDate(filterDateOptions["absoluteStart"]));
    const [localAbsoluteEndDate, setLocalAbsoluteEndDate] = useState<string>(getDateStrFromDate(filterDateOptions["absoluteEnd"]));
    const [localAbsoluteEndTime, setLocalAbsoluteEndTime] = useState<string>(getTimeStrFromDate(filterDateOptions["absoluteEnd"]));
    const [rangeError, setRangeError] = useState<string | null>(null);

    useEffect(() => {
        function handleClickOutside(event: any) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShow(false);
            }
        }
        // Bind the event listener
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [wrapperRef]);

    const validateTimeRange = (startDate: Date, endDate: Date): boolean => {
        if (!maxRangeInDays) return true;

        const diffInMs = endDate.getTime() - startDate.getTime();
        const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

        if (diffInDays > maxRangeInDays) {
            setRangeError(`Time range cannot exceed ${maxRangeInDays} days`);
            return false;
        }

        setRangeError(null);
        return true;
    };

    const handleAbsoluteTimeUpdate = (startOrEnd: string, absoluteTime: string) => {
        const d = new Date(absoluteTime);
        if (startOrEnd === "start") {
            setLocalAbsoluteStartDate(getDateStrFromDate(d));
            setLocalAbsoluteStartTime(getTimeStrFromDate(d));
        } else {
            setLocalAbsoluteEndDate(getDateStrFromDate(d));
            setLocalAbsoluteEndTime(getTimeStrFromDate(d));
        }
    };

    const clearTime = () => {
        setFilterDateOptions({
            dateOptionType: DateType.Absolute,
            relativeStart: null,
            relativeStartScale: "d",
            relativeEnd: 0,
            relativeEndScale: "d",
            absoluteStart: null,
            absoluteEnd: new Date(),
        });
        setRangeError(null);
        setShow(false);
    };

    const applyTime = () => {
        const startDate = localType === DateType.Relative 
            ? getAbsoluteTimeFromRelativeTime(localRelativeStart + localRelativeStartScale) 
            : getLocalizedDate(localAbsoluteStartDate, localAbsoluteStartTime);
        
        const endDate = localType === DateType.Relative 
            ? getAbsoluteTimeFromRelativeTime(localRelativeEnd + localRelativeEndScale) 
            : getLocalizedDate(localAbsoluteEndDate, localAbsoluteEndTime);

        if (!validateTimeRange(startDate, endDate)) {
            return;
        }

        setFilterDateOptions({
            dateOptionType: localType,
            relativeStart: localRelativeStart,
            relativeStartScale: localRelativeStartScale,
            relativeEnd: localRelativeEnd,
            relativeEndScale: localRelativeEndScale,
            absoluteStart: startDate,
            absoluteEnd: endDate,
        });
        setRangeError(null);
        setShow(false);
    };

    const handleRelativeStartChange = (event: any) => {
        setLocalRelativeStart(parseFloat(event.target.value));
        const updatedStartTime = getAbsoluteTimeFromRelativeTime(event.target.value + localRelativeStartScale);
        if (!isNaN(updatedStartTime.getTime())) {
            handleAbsoluteTimeUpdate('start', updatedStartTime.toISOString());
        }
    };

    const handleRelativeStartUnitChange = (k: any) => {
        setLocalRelativeStartScale(k);
        handleAbsoluteTimeUpdate('start', getAbsoluteTimeFromRelativeTime(localRelativeStart + k).toISOString())
    };

    const handleRelativeEndChange = (event: any) => {
        setLocalRelativeEnd(parseFloat(event.target.value));
        const updatedEndTime = getAbsoluteTimeFromRelativeTime(event.target.value + localRelativeEndScale);
        if (!isNaN(updatedEndTime.getTime())) {
            handleAbsoluteTimeUpdate('end', updatedEndTime.toISOString())
        }
    };

    const handleRelativeEndUnitChange = (k: any) => {
        setLocalRelativeEndScale(k);
        handleAbsoluteTimeUpdate('end', getAbsoluteTimeFromRelativeTime(localRelativeEnd + k).toISOString())
    }

    return (
        <div ref={wrapperRef} className="filter-select-container" style={{left: '0%'}}>
            <Tabs 
                id="date-selector-picker"
                onSelect={(k: any) => setLocalType(k)}
                activeKey={localType} 
                className="m-3"
            >
                <Tab eventKey="absolute" title="Absolute">
                    <div style={{minWidth: '500px'}}>
                        <Container>
                            {rangeError && (
                                <Row className="mb-2">
                                    <Col>
                                        <Alert variant="danger" className="py-2 mb-0">
                                            {rangeError}
                                        </Alert>
                                    </Col>
                                </Row>
                            )}
                            <Row>
                                <Col md={6}>
                                    <h6>Start</h6>
                                    <Form.Control type="date" name='startDate' value={localAbsoluteStartDate} onChange={(event: any) => {setLocalAbsoluteStartDate(event.target.value)}} />
                                    <Form.Control className="mt-1" type="time" name='startTime' value={localAbsoluteStartTime} onChange={(event: any) => {setLocalAbsoluteStartTime(event.target.value)}} />
                                </Col>
                                <Col md={6}>
                                    <h6>End</h6>
                                    <Form.Control type="date" name='endDate' value={localAbsoluteEndDate} onChange={(event: any) => {setLocalAbsoluteEndDate(event.target.value)}} />
                                    <Form.Control className="mt-1" type="time" name='endTime' value={localAbsoluteEndTime} onChange={(event: any) => {setLocalAbsoluteEndTime(event.target.value)}} />
                                </Col>
                            </Row>
                            <Row className="pt-2 pb-1">
                                <Col md={8}>
                                    <Form.Text muted>
                                        Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}
                                        {maxRangeInDays && ` | Max range: ${maxRangeInDays} days`}
                                    </Form.Text>
                                </Col>
                            </Row>
                            <Row className="pt-3 pb-3">
                                <Col md={6}>
                                    <Button variant="outline-secondary" size="sm" onClick={() => clearTime()}>Clear Filter</Button>
                                </Col>
                                <Col md={6} className='text-right'>
                                    <Button variant="secondary" size="sm" onClick={() => applyTime()}>Apply</Button>
                                </Col>
                            </Row>
                        </Container>
                    </div>
                </Tab>
                <Tab eventKey="relative" title="Relative">
                    <div style={{minWidth: '500px'}}>
                        <Container>
                            {rangeError && (
                                <Row className="mb-2">
                                    <Col>
                                        <Alert variant="danger" className="py-2 mb-0">
                                            {rangeError}
                                        </Alert>
                                    </Col>
                                </Row>
                            )}
                            <Row>
                                <Col md={6}>
                                    <h6>Start</h6>

                                    <InputGroup>
                                        <Form.Control type="number" aria-label="Start" value={localRelativeStart === null ? undefined : localRelativeStart} onChange={(event: any) => {handleRelativeStartChange(event)}} />

                                        <DropdownButton
                                            variant="outline-secondary"
                                            title={TIME_SCALE_MAP[localRelativeStartScale]}
                                            align="end"
                                        >
                                            {
                                                Object.keys(TIME_SCALE_MAP).map((k: any) => {
                                                    return (
                                                        <Dropdown.Item key={k} onClick={() => handleRelativeStartUnitChange(k)}>{TIME_SCALE_MAP[k]}</Dropdown.Item>
                                                    )
                                                })
                                            }
                                        </DropdownButton>
                                    </InputGroup>
                                </Col>
                                <Col md={6}>
                                    <h6>End</h6>

                                    <InputGroup>
                                        <Form.Control type="number" aria-label="End" value={localRelativeEnd} onChange={(event: any) => {handleRelativeEndChange(event)}} />

                                        <DropdownButton
                                            variant="outline-secondary"
                                            title={TIME_SCALE_MAP[localRelativeEndScale]}
                                            align="end"
                                        >
                                            {
                                                Object.keys(TIME_SCALE_MAP).map((k: any) => {
                                                    return (
                                                        <Dropdown.Item key={k} onClick={() => {handleRelativeEndUnitChange(k)}}>{TIME_SCALE_MAP[k]}</Dropdown.Item>
                                                    )
                                                })
                                            }
                                        </DropdownButton>
                                    </InputGroup>
                                </Col>
                            </Row>
                            <Row className="pt-2 pb-1">
                                <Col md={8}>
                                    <Form.Text muted>
                                        {maxRangeInDays && `Max range: ${maxRangeInDays} days`}
                                    </Form.Text>
                                </Col>
                            </Row>
                            <Row className="pt-3 pb-3">
                                <Col md={6}>
                                    <Button variant="outline-secondary" size="sm" onClick={() => clearTime()}>Clear Filter</Button>
                                </Col>
                                <Col md={6} className='text-right'>
                                    <Button variant="secondary" size="sm" onClick={() => applyTime()}>Apply</Button>
                                </Col>
                            </Row>
                        </Container>
                    </div>
                </Tab>
            </Tabs>
        </div>
    );
};
